import { getAuth } from 'firebase/auth';

// En la Mac puede apuntar al emulador (REACT_APP_FUNCTIONS_URL, ver README "Probar en local").
const FUNCTIONS_URL = process.env.REACT_APP_FUNCTIONS_URL || 'https://us-central1-transferisok.cloudfunctions.net';

// Clave de sessionStorage donde queda a quién está mirando el superadmin. No va en
// la URL: el Meta Pixel manda la URL completa a Meta y filtraría emails de clientes.
const CLAVE_VER_COMO = 'transferisok.verComo';

export const ROLES = {
  principal: 'Admin principal',
  administrador: 'Administrador',
  operador: 'Operador'
};

async function llamar(funcion, body = {}) {
  const usuario = getAuth().currentUser;
  if (!usuario) throw new Error('No hay usuario autenticado');

  const response = await fetch(`${FUNCTIONS_URL}/${funcion}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${await usuario.getIdToken()}`
    },
    body: JSON.stringify(body)
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || 'No se pudo completar la operación');
  }
  return data;
}

export const adminService = {
  // El rol viene en el token como custom claim (scripts/hacer-superadmin.js). Se
  // pide el token renovado para que un claim recién puesto se vea al recargar.
  async esSuperadmin(usuario) {
    try {
      const { claims } = await usuario.getIdTokenResult(true);
      return claims.superadmin === true;
    } catch (error) {
      console.error('No se pudo leer el rol del usuario:', error);
      return false;
    }
  },

  // [{ collector_id, nombre, mp: { tipo, status }, usuarios: [...] }]
  async listarNegocios() {
    return (await llamar('adminUsuarios')).negocios || [];
  },

  async simularBaja(email) {
    return (await llamar('adminDarDeBaja', { email })).plan;
  },

  async darDeBaja(email, confirmacion) {
    return llamar('adminDarDeBaja', { email, aplicar: true, confirmacion });
  },

  // Monto máximo que ven los operadores de la cuenta (null = sin límite).
  async fijarLimite(collectorId, limite) {
    return llamar('adminFijarLimite', { collector_id: collectorId, limite });
  },

  // Alias de la cuenta de MP que el home le muestra a todos (null = sin alias).
  async fijarAlias(collectorId, alias) {
    return llamar('adminFijarAlias', { collector_id: collectorId, alias });
  },

  // Seguimiento de la suscripción de un negocio (functions/src/suscripciones):
  // accion = iniciar | cambiar_inicio | extender | bonificar | quitar_bonificacion.
  // Devuelve el resumen actualizado.
  async suscripcion(collectorId, accion, datos = {}) {
    return (await llamar('adminSuscripcion', { collector_id: collectorId, accion, ...datos })).suscripcion;
  },

  // Rol y monto máximo de un operador de otra cuenta (null = sin límite propio).
  async editarOperador(email, { admin, limite_monto }) {
    return llamar('adminEditarOperador', { email, admin, limite: limite_monto });
  },

  // Para el admin de una cuenta (no hace falta ser superadmin): el límite que ven sus
  // operadores sin uno propio (null = ven todas).
  async limiteDeLaCuenta() {
    return (await llamar('limiteDeLaCuenta')).limite_operadores ?? null;
  },

  // Para el admin de una cuenta, desde Mi Perfil: el alias de Mercado Pago que el home
  // les muestra a todos (null = sacarlo). Devuelve { alias, mostrar } como quedó.
  async fijarAliasDeLaCuenta(alias) {
    return llamar('fijarAliasDeLaCuenta', { alias });
  },

  // "Mostrar alias en el panel" de Mi Perfil. Devuelve { alias, mostrar } como quedó.
  async mostrarAliasDeLaCuenta(mostrar) {
    return llamar('fijarAliasDeLaCuenta', { mostrar });
  },

  leerVerComo() {
    try {
      return sessionStorage.getItem(CLAVE_VER_COMO) || null;
    } catch (error) {
      return null;
    }
  },

  guardarVerComo(email) {
    try {
      if (email) {
        sessionStorage.setItem(CLAVE_VER_COMO, email);
      } else {
        sessionStorage.removeItem(CLAVE_VER_COMO);
      }
    } catch (error) {
      // Sin sessionStorage la vista no sobrevive a una recarga, nada más.
    }
  }
};
